import React, { useRef, useEffect } from "react";
import { graphql } from "gatsby";
import { Dialog } from "@headlessui/react";
import { GatsbyImage } from "gatsby-plugin-image";
import Player from "react-player";
import {
  MdFullscreenExit,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";

const MediaGallery: React.FC<
  | {
      open: boolean;
      multiple: false;
      media:
        | Queries.MediaGalleryImageFragment
        | Queries.MediaGalleryVideoFragment;
      handleClose: () => void;
    }
  | {
      open: boolean;
      multiple: true;
      media: ReadonlyArray<
        Queries.MediaGalleryImageFragment | Queries.MediaGalleryVideoFragment
      >;
      activeMedia: number;
      handleNext: () => void;
      handlePrevious: () => void;
      handleClose: () => void;
    }
> = (props) => {
  const prevOpen = useRef(false);

  useEffect(() => {
    prevOpen.current = props.open;
  }, [props.open]);

  const activeMediaItem =
    props.multiple === true ? props.media[props.activeMedia] : props.media;

  if (!activeMediaItem) return null;

  return (
    <Dialog open={props.open} onClose={props.handleClose}>
      <Dialog.Panel className="fixed inset-0 z-[100] bg-black/95">
        <div className="grid h-full w-full grid-rows-[1fr,auto] lg:p-16">
          <div className="relative h-full w-full">
            <div className="absolute inset-0 m-auto aspect-video max-h-full max-w-full">
              {activeMediaItem.__typename === "ContentfulAsset" && (
                <GatsbyImage
                  alt={activeMediaItem.title}
                  image={activeMediaItem.gatsbyImageData}
                  className="absolute inset-0"
                />
              )}
              {activeMediaItem.__typename === "ContentfulVimeoVideo" && (
                <Player
                  controls
                  url={activeMediaItem.url}
                  width="inherit"
                  height="inherit"
                  config={{
                    vimeo: {
                      playerOptions: {
                        // If the media gallery was just opened and the active media is a video then autoplay it
                        autoplay: prevOpen.current === false,
                        // Automatically enable closed captions (only works for autogenerated captions)
                        texttrack: "en-x-autogen",
                        // Set the primary color of the controls
                        color: "#f8b332",
                        // Enable the video speed option
                        speed: true,
                        // Disable picture-in-picture mode
                        pip: false,
                        // Disable the video creators portrait (Logo)
                        portrait: false,
                        // Disable the video title
                        title: false,
                        // Disable the video creators name (CLHbid)
                        byline: false,
                      },
                    },
                  }}
                  className="absolute inset-0"
                />
              )}
            </div>
          </div>
          {activeMediaItem?.description && (
            <p className="m-0 block pt-2 text-center text-white">
              {activeMediaItem.description}
            </p>
          )}
        </div>
        <button
          aria-label="close"
          onClick={props.handleClose}
          className="absolute right-2 top-2 z-10 rounded bg-gray-900 p-1"
        >
          <MdFullscreenExit className="h-10 w-10 fill-clhbid-orange" />
        </button>
        {props.multiple && (
          <>
            <div className="absolute left-0 right-0 top-2 text-center text-white">
              <div className="mx-auto w-fit rounded bg-gray-900 px-4 py-3">
                {props.activeMedia + 1} / {props.media.length}
              </div>
            </div>
            <button
              aria-label="previous"
              onClick={props.handlePrevious}
              className="absolute left-2 rounded bg-gray-900 p-1 portrait:bottom-2 landscape:top-[calc(50%-24px)]"
            >
              <MdKeyboardArrowLeft className="h-10 w-10 fill-clhbid-orange" />
            </button>
            <button
              aria-label="next"
              onClick={props.handleNext}
              className="absolute right-2 rounded bg-gray-900 p-1 portrait:bottom-2 landscape:top-[calc(50%-24px)]"
            >
              <MdKeyboardArrowRight className="h-10 w-10 fill-clhbid-orange" />
            </button>
          </>
        )}
      </Dialog.Panel>
    </Dialog>
  );
};

export const MediaGalleryFragment = graphql`
  fragment MediaGalleryImage on ContentfulAsset {
    __typename
    title
    description
    gatsbyImageData
  }

  fragment MediaGalleryVideo on ContentfulVimeoVideo {
    __typename
    title
    description
    url
  }
`;

export default MediaGallery;
